import { Component, Input } from '@angular/core';
import moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';

import { DATE_FORMAT_SHAREPOINT } from '@shared-constants/date-format-constants';
import { COST_CENTER_PATTERN, WEEK_ENDING_DATE_PATTERN } from '@shared-constants/pattern-constants';
import { trimLeadingZeros } from '@shared-helpers/string-number-helpers';

import { environment } from '../../../environments/environment';
import { BillingTimesheet } from '../../scripts/BillingTimesheet';
import { ExtDocument } from '../../scripts/extDocument';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'single-file-upload',
  templateUrl: './single-file-upload.component.html',
  standalone: false,
})
export class SingleFileUploadComponent {
  @Input() timesheet: BillingTimesheet;
  @Input() currentSharepointUploadData: ExtDocument;
  @Input() isAttachButtonEnabled: boolean;
  base64Output: string;
  file: File | null = null; // Variable to store file

  constructor(
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
    private readonly apiService: ApiService,
  ) {}

  // On file Select
  onChange(tgt: HTMLInputElement) {
    const file: File = tgt.files[0];
    if (file) {
      if (file.size > 23385000) {
        let currentSharepointLink = environment.sharepointBaseUrl;
        if (this.timesheet.sharepointFiles.length > 0) {
          currentSharepointLink = currentSharepointLink + this.timesheet.sharepointFiles[0].url;
        }
        this.modalService.launchResponsiveModal('simple-text', {
          title: 'File Size Too Large',
          body: 'Please click "Go To Sharepoint" to upload.',
          buttonText: 'Go To Sharepoint',
          buttonAction: 'openLink',
          webpageUrl: currentSharepointLink.split('/').slice(0, -1).join('/'),
        });
      } else {
        this.file = file;
      }
    }

    this.convertFile$(this.file).subscribe({
      next: (base64) => {
        this.base64Output = base64;
        this.onUpload();
      },
      error: (err) => {
        console.error('Error in convertFile subscription:', err);
      },
    });
  }

  private onUpload(): void {
    const costCenter: string = this.timesheet.contractCostCenter;
    const weekEndingDate: string = this.timesheet.weDate.selectedDate.value.format(DATE_FORMAT_SHAREPOINT);
    const custGrp = this.timesheet.contractCustGrp;
    const timesheetNo = trimLeadingZeros(this.currentSharepointUploadData.Documentno);
    const fileName = `${timesheetNo}.${this.file.name}`;
    const splitName = fileName.split('.');
    const displayName = splitName.slice(1, -1);
    const fileType = `.${splitName.splice(-1)[0]}`;
    const data = this.base64Output;

    if (this.file && this.isValid(data, fileName, costCenter, weekEndingDate)) {
      this.timesheet.addSharepointFile(fileName, displayName, fileType, timesheetNo);
      const currentSpFile = this.timesheet.sharepointFiles.find((spf) => spf.fileName === fileName);
      this.apiService.uploadSharepointFile$(costCenter, weekEndingDate, custGrp, fileName, data).subscribe({
        next: (sp) => {
          currentSpFile.url = sp.webUrl;
          currentSpFile.weekendingDate = moment(sp.lastModifiedDateTime).format('MM-DD-YY');
          currentSpFile.hasBeenUploaded = true;
          currentSpFile.canEdit = false;
          currentSpFile.canDelete = true;
          currentSpFile.canView = true;
        },
        error: () => {
          this.timesheet.deleteSharepointFile(weekEndingDate, currentSpFile);
        },
      });
    }
  }

  private isValid(data: string, fileName: string, costCenter: string, weekEndingDate: string): boolean {
    const hasMatches = COST_CENTER_PATTERN.test(costCenter); // true is costCenter matches, false if not
    const isDateValid = WEEK_ENDING_DATE_PATTERN.test(weekEndingDate); // checks that a date is in format 'MM_DD_YY'
    const doesNameExist = this.timesheet.sharepointFiles.some((file) => file.fileName === fileName && file.shouldShow); // true if any file already has name, false if not

    if (data == null || data === '') {
      this.datahub.messageService.warning('This file appears to be empty. Please choose another file.');
      return false;
    }

    if (doesNameExist) {
      this.datahub.messageService.warning('File already exists, please delete existing file before uploading.');
      return false;
    }
    if (!costCenter) {
      this.datahub.messageService.warning('You must provide a cost center');
      return false;
    }
    if (!hasMatches) {
      this.datahub.messageService.warning("Cost Center does not have 6 digits or isn't a string");
      return false;
    }
    if (!weekEndingDate) {
      this.datahub.messageService.warning('You must provide a week ending date');
      return false;
    }
    if (!isDateValid) {
      this.datahub.messageService.warning(`Incorrect date, ensure date passed is in ${DATE_FORMAT_SHAREPOINT} format`);
      return false;
    }

    return true;
  }

  private convertFile$(file: File): Observable<string> {
    const resultSubject$ = new ReplaySubject<string>(1);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      //https://stackoverflow.com/a/56249846/79677
      const dataUrl = reader.result;
      if (typeof dataUrl === 'string') {
        const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
        resultSubject$.next(base64);
      }
    };
    return resultSubject$;
  }
}
